/**
 * 通用网络请求类
 * 统一管理所有网络请求
 */
import * as fileUtil from "../util/file.js";
import { createLogger } from "../util/logger.js";
import { triggerFileDownload } from "../util/file.js";
import uploadStorage from "../util/uploadStorage.js";
import { initBehaviorLog, setBehaviorLogAuthToken, reportBehaviorLog, reportException } from "../util/behaviorLog.js";

const logger = createLogger("Request");

class Request {
  
  constructor() {
    const baseURL = import.meta.env.VITE_APP_API_BASE_URL || ''
    const basePath = import.meta.env.VITE_APP_BASE_PATH || ''
    this.baseURL = baseURL;
    this.basePath = basePath;
    this.timeout = 10000; // 10秒超时
    this.defaultHeaders = {
      // "Content-Type": "application/json",
      "Content-Type": "application/json;charset=UTF-8",
      // Accept: "application/json, text/plain, */*",
    };
  }

  /**
   * 设置基础URL
   * @param {string} baseURL 基础URL
   */
  setBaseURL(baseURL) {
    this.baseURL = baseURL;
  }

  /**
   * 设置默认请求头
   * @param {object} headers 请求头
   */
  setDefaultHeaders(headers) {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers };
  }

  /**
   * 设置超时时间
   * @param {number} timeout 超时时间（毫秒）
   */
  setTimeout(timeout) {
    this.timeout = timeout;
  }

  /**
   * 构建完整的URL
   * @param {string} url 请求URL
   * @returns {string} 完整URL
   */
  buildURL(url) {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return this.baseURL + url;
  }

  /**
   * 构建请求配置
   * @param {object} options 请求选项
   * @returns {object} 请求配置
   */
  buildRequestConfig(options = {}) {
    const {
      method = "GET",
      headers = {},
      body = null,
      timeout = this.timeout,
    } = options;

    const config = {
      method: method.toUpperCase(),
      headers: { ...this.defaultHeaders, ...headers },
      timeout,
    };

    // 删除值为 null 或 undefined 的头部（用于移除某些默认头部）
    Object.keys(config.headers).forEach((key) => {
      if (config.headers[key] === null || config.headers[key] === undefined) {
        delete config.headers[key];
      }
    });

    // 处理请求体
    if (body) {
      if (body instanceof FormData) {
        // FormData不需要设置Content-Type，浏览器会自动设置
        delete config.headers["Content-Type"];
        config.body = body;
      } else if (typeof body === "object") {
        config.body = JSON.stringify(body);
      } else {
        config.body = body;
      }
    }

    return config;
  }

  /**
   * 处理响应
   * @param {Response} response 响应对象
   * @param {boolean} isRetry 是否是重试请求（防止无限循环）
   * @returns {Promise<{success: boolean, data: any, error: string|null}>} 处理后的结果
   */
  async handleResponse(response, isRetry = false) {
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }

      return {
        success: false,
        data: null,
        error:
          errorData.message ||
          `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    const contentType = response.headers.get("content-type");
    try {
      if (contentType && contentType.includes("application/json")) {
        const responseData = await response.json();
        
        // 处理统一返回格式：{code, message, timestamp, data}
        if (responseData && typeof responseData === 'object' && 'code' in responseData) {
          // 检查业务状态码
          if (responseData.code === '200' || responseData.code === 200) {
            // 成功：返回 data 字段的内容
            return {
              success: true,
              data: responseData.data,
              error: responseData.message,
            };
          } else if ((responseData.code === '600' || responseData.code === 600) && !isRetry) {
            // code 为 600 时，标记需要重新登录
            return {
              success: false,
              data: responseData.data,
              error: responseData.message || `业务处理失败，错误码: ${responseData.code}`,
              isRetry: true,
            };
          } else {
            // 业务失败：返回错误信息
            return {
              success: false,
              data: responseData.data,
              error: responseData.message || `业务处理失败，错误码: ${responseData.code}`,
            };
          }
        } else {
          // 非标准格式，直接返回
          return {
            success: true,
            data: responseData.data,
            error: responseData.message,
          };
        }
      } else {
        const data = await response.text();
        return {
          success: true,
          data,
          error: null,
        };
      }
    } catch (error) {
      return {
        success: false,
        data: null,
        error: "解析响应数据失败",
      };
    }
  }

  /**
   * 发送请求
   * @param {string} url 请求URL
   * @param {object} options 请求选项
   * @param {boolean} isRetry 是否是重试请求（防止无限循环）
   * @returns {Promise<{success: boolean, data: any, error: string|null}>} 请求结果
   */
  async request(url, options = {}, isRetry = false) {
    const fullURL =
      url.startsWith("http://") || url.startsWith("https://")
        ? url
        : this.buildURL(url);
    const config = this.buildRequestConfig(options);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.timeout);

      const response = await fetch(fullURL, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      let responseResult = await this.handleResponse(response, isRetry);
      if (responseResult.isRetry) {
        return await this.request(url, options, true);
      }
      return responseResult;
    } catch (error) {
      if (error.name === "AbortError") {
        return {
          success: false,
          data: null,
          error: "请求超时",
        };
      }
      return {
        success: false,
        data: null,
        error: error.message || "网络请求失败",
      };
    }
  }

  /**
   * GET请求
   * @param {string} url 请求URL
   * @param {object} params 查询参数
   * @param {object} options 请求选项
   * @returns {Promise<{success: boolean, data: any, error: string|null}>} 请求结果
   */
  async get(url, params = {}, options = {}) {
    const queryString =
      params && Object.keys(params).length > 0
        ? "?" + new URLSearchParams(params).toString()
        : "";
    const fullURL = queryString ? `${url}${queryString}` : url;

    return this.request(fullURL, {
      method: "GET",
      ...options,
    });
  }

  /**
   * POST请求
   * @param {string} url 请求URL
   * @param {object} data 请求数据
   * @param {object} options 请求选项
   * @returns {Promise<{success: boolean, data: any, error: string|null}>} 请求结果
   */
  async post(url, data = null, options = {}) {
    return this.request(url, {
      method: "POST",
      body: data,
      ...options,
    });
  }

  /**
   * PUT请求
   * @param {string} url 请求URL
   * @param {object} data 请求数据
   * @param {object} options 请求选项
   * @returns {Promise<{success: boolean, data: any, error: string|null}>} 请求结果
   */
  async put(url, data = null, options = {}) {
    return this.request(url, {
      method: "PUT",
      body: data,
      ...options,
    });
  }

  /**
   * DELETE请求
   * @param {string} url 请求URL
   * @param {object} options 请求选项
   * @returns {Promise<{success: boolean, data: any, error: string|null}>} 请求结果
   */
  async delete(url, options = {}) {
    return this.request(url, {
      method: "DELETE",
      ...options,
    });
  }

  /**
   * 获取文件下载链接
   * @param {Object|string} fileItemOrFileId - 文件项（包含 fileId）或文件ID字符串
   * @returns {Promise<string|null>} 下载链接，失败返回null
   */
  async getFileDownloadUrl(fileItemOrFileId) {
    try {
      // 支持两种参数格式：文件对象或文件ID字符串
      const fileId =
        typeof fileItemOrFileId === "string"
          ? fileItemOrFileId
          : fileItemOrFileId?.fileId || null;

      if (!fileId) {
        return null;
      }

      const params = {
        fileId: fileId,
      };

      const result = await this.get( "/api/share/file/getDownloadUrl", params );

      if (result.success && result.data && result.data.url) {
        return result.data.url;
      }

      return null;
    } catch (error) {
      logger.error("获取下载链接失败:", error);
      return null;
    }
  }

  /**
   * 下载文件
   * @param {string} url - 下载URL
   * @param {string} fileName - 文件名
   * @returns {Promise<{success: boolean, error: string|null}>} 下载结果
   */
  async downloadFile(url, fileName) {
    try {
      if (!url || url === "") {
        return {
          success: false,
          error: "下载URL为空",
        };
      }

      // logger.debug("原始下载URL:", url);

      // 将下载URL替换为代理路径，通过代理设置Host和Referer
      // 确保URL格式正确：https://cn-beijing-data.aliyundrive.net/xxx -> /api/download/xxx
      // 如果部署在子路径下（如 /yp），需要包含 basePath
      let downloadUrl = fileUtil.backToDownloadUrl(url);

      triggerFileDownload(downloadUrl, fileName);
      return {
        success: true,
        error: null,
      };
    } catch (error) {
      logger.error("下载失败:", error);
      return {
        success: false,
        error: "异常，请联系商家处理！",
      };
    }
  }

  /**
   * 创建文件（上传文件）
   * @param {Object} params - 参数对象
   * @param {File} params.file - 文件对象
   * @param {string} params.parentFileId - 父文件夹ID
   * @param {Function} onProgress - 进度回调函数
   * @param {Function} onContentProgress - 内容上传进度回调函数
   * @param {Function} [onChunkDone] - 单个分片上传完成回调 (chunkSizeBytes, durationMs) => void，用于按分片耗时计算速度
   * @param {Function} onCancelCheck - 取消检查函数
   * @returns {Promise<{success: boolean, data: any, fileId: string|null, error: string|null}>} 请求结果
   */
  async createFileWithFolders(
    params,
    onProgress,
    onContentProgress,
    onChunkDone,
    onCancelCheck,
    isRetry = false
  ) {
    const { fileItem, parentFileId = "" } = params;

    const file = fileItem.file;

    // 自动生成分片信息
    const finalPartInfoList = fileUtil.generatePartInfoList(file.size);

    // 自动判断是否为大文件（大于1MB为大文件）
    const finalIsLarge = file.size > 1024 * 1024;

    const uploadParams = {
      checkNameMode: "auto_rename",
      name: file.name,
      parallelUpload: false,
      parentFileId: parentFileId,
      size: file.size,
      type: "file",
      partInfoList: finalPartInfoList,
      createScene: "file_upload",
      localModifiedAt: new Date().toISOString(),
      contentType: "application/oct-stream"
    };

    // 大文件使用预秒传，小文件使用验证码
    if (finalIsLarge && !isRetry) {
      const preHash = await fileUtil.calculatePreHash(file);
      if (preHash) {
        uploadParams.preHash = preHash;
      }
    } else {
      const proofCode = await fileUtil.generateProofCodeFromAccessToken(file);
      if (proofCode) {
        uploadParams.proofCode = proofCode;
        uploadParams.proofVersion = "v1";
      }
    }

    const options = {};
    if (onProgress) {
      options.onProgress = onProgress;
    }

    // 创建文件请求（此请求可能较耗时，期间 UI 显示「校验中」）
    if (params.fileItem) {
      params.fileItem.isPreparing = true;
    }
    let result = await this.post(
      "/api/file/uploadSmallFile",
      uploadParams,
      options
    );

    // 预秒传失败，回退为完整SHA1上传
    if (!result.success && finalIsLarge && !isRetry) {
      return this.createFileWithFolders(params, onProgress, onContentProgress, onChunkDone, onCancelCheck, true);
    }

    // 非重试路径：校验结束，可进入上传中
    if (params.fileItem) {
      params.fileItem.isPreparing = false;
    }

    if (!result.success) {
      reportBehaviorLog({
        action: "upload_create_fail",
        description: `name=${file?.name || ""}, size=${file?.size || 0}, error=${result?.error || ""}`,
      });
      return result;
    }

    const response = result.data;
    const fileId = response.fileId || null;
    const uploadId = response.uploadId || null;

    // 同步更新 fileItem 的 fileId
    if (fileId && params.fileItem) {
      params.fileItem.fileId = fileId;
      params.fileItem.uploadId = uploadId;
    }

    // 如果没有分片信息直接返回上传成功
    if (!response.partInfoList || response.partInfoList.length === 0) {
      return {
        success: true,
        data: response,
        fileId: fileId,
        error: null,
      };
    }

    // 为 partInfoList 中的每个分片添加 chunk 参数（用累加偏移计算 start，避免最后一片 partSize 较小时取错区间）
    let offset = 0;
    const partInfoListWithChunks = response.partInfoList.map(partInfo => {
      const partSize = partInfo.partSize;
      const start = offset;
      offset += partSize;
      const end = Math.min(start + partSize, file.size);
      const chunk = file.slice(start, end);
      return {
        ...partInfo,
        chunk: chunk
      };
    });

    // 更新 response 中的 partInfoList
    response.partInfoList = partInfoListWithChunks;

    // 将 partInfoList 赋值到 fileItem 中
    if (params.fileItem) {
      params.fileItem.partInfoList = partInfoListWithChunks;
    }

    // 存 chunk 改为后台执行，不阻塞上传，用户无感直接开始上传
    if (fileId && uploadId && response.partInfoList) {
      const fileItem = params.fileItem;
      uploadStorage
        .saveUploadDataWithChunks(
          fileId,
          { uploadId, partInfoList: response.partInfoList },
          undefined, // 不再回调解校验中进度
          onCancelCheck
        )
        .then(() => {
          logger.info(`已保存上传数据到本地存储: fileId=${fileId}, uploadId=${uploadId}`);
          if (fileItem) {
            return uploadStorage.saveFileInfo(fileId, {
              id: fileItem.id,
              name: fileItem.name,
              size: fileItem.size,
              type: fileItem.type,
              addedAt: fileItem.addedAt || new Date().toLocaleString(),
              uploadStatus: fileItem.uploadStatus,
              progress: fileItem.progress || 0,
              uploadedSize: fileItem.uploadedSize || 0,
              downloadCount: fileItem.downloadCount || 0,
            });
          }
        })
        .catch((error) => {
          logger.error("保存上传数据到本地存储失败:", error);
          if (fileId) {
            uploadStorage.deleteUploadData(fileId).catch(() => {});
          }
          reportException(error, {
            action: "upload_storage_save_fail",
            description: `name=${file?.name || ""}, fileId=${fileId || ""}`,
          });
        });
    }

    // 如果有分片信息，调用 dealUploadPartInfo 处理分片上传
    return await this.dealUploadPartInfo(
      response,
      params,
      onContentProgress,
      onChunkDone,
      onCancelCheck
    );
  }

  /**
   * 续传 / 403 重试：先获取最新上传地址，再执行 dealUploadPartInfo
   * @param {Object} response - 含 fileId、uploadId、partInfoList（续传时可为 null，从存储读取）
   * @param {Object} params - 上传参数（包含 fileItem）
   * @param {Function} onContentProgress - 内容上传进度回调
   * @param {Function} [onChunkDone] - 单个分片上传完成回调 (chunkSizeBytes, durationMs) => void
   * @param {Function} onCancelCheck - 取消检查回调
   * @returns {Promise<{success: boolean, data: any, fileId: string|null, error: string|null}>}
   */
  async resumeUploadWithLatestUrls(response, params, onContentProgress, onChunkDone, onCancelCheck) {
    const fileId = response?.fileId || null;
    let uploadId = response?.uploadId || null;
    let savedUploadData = null;

    if (response?.partInfoList?.length) {
      savedUploadData = { ...response };
    } else if (fileId) {
      const storedData = await uploadStorage.getUploadData(fileId);
      if (storedData?.uploadId && storedData?.partInfoList?.length) {
        savedUploadData = {
          fileId: storedData.fileId,
          uploadId: storedData.uploadId,
          partInfoList: storedData.partInfoList,
        };
        if (!uploadId) uploadId = storedData.uploadId;
      }
    }

    if (savedUploadData?.fileId && savedUploadData?.uploadId && savedUploadData?.partInfoList?.length) {
      const urlResult = await this.getUploadUrl(
        savedUploadData.fileId,
        savedUploadData.uploadId,
        savedUploadData.partInfoList
      );
      if (!urlResult.success || !urlResult.partInfoList?.length) {
        return {
          success: false,
          data: null,
          fileId: savedUploadData.fileId,
          error: urlResult.error || "上传失败",
        };
      }
      // 按分片号合并：保留原 partInfoList（含 chunk、partNumber 等），仅用接口返回的 URL 更新 uploadUrl（兼容 part_number/upload_url）
      const urlByPart = {};
      urlResult.partInfoList.forEach((item) => {
        const num = item.part_number ?? item.partNumber;
        const url = item.upload_url ?? item.uploadUrl;
        if (num != null && url) urlByPart[num] = url;
      });
      savedUploadData.partInfoList.forEach((p) => {
        const num = p.partNumber ?? p.part_number;
        const url = urlByPart[num];
        if (url) p.uploadUrl = url;
        if (p.partNumber == null && p.part_number != null) p.partNumber = p.part_number;
      });
      try {
        // 只更新元数据（含最新 uploadUrl），不重复存 chunk
        const partInfoListForMeta = savedUploadData.partInfoList.map(({ chunk, ...rest }) => rest);
        await uploadStorage.saveUploadData(savedUploadData.fileId, {
          uploadId: savedUploadData.uploadId,
          partInfoList: partInfoListForMeta,
        });
        if (params.fileItem) {
          await uploadStorage.saveFileInfo(savedUploadData.fileId, {
            id: params.fileItem.id,
            name: params.fileItem.name,
            size: params.fileItem.size,
            type: params.fileItem.type,
            addedAt: params.fileItem.addedAt || new Date().toLocaleString(),
            uploadStatus: params.fileItem.uploadStatus,
            progress: params.fileItem.progress || 0,
            uploadedSize: params.fileItem.uploadedSize || 0,
            downloadCount: params.fileItem.downloadCount || 0,
          });
        }
      } catch (saveErr) {
        logger.warn("续传前更新存储失败:", saveErr);
      }
      logger.info('savedUploadData:', savedUploadData.partInfoList)
      return this.dealUploadPartInfo(savedUploadData, params, onContentProgress, onChunkDone, onCancelCheck);
    }else{
      return this.dealUploadPartInfo(response, params, onContentProgress, onChunkDone, onCancelCheck);
    }

    
  }

  /**
   * 处理分片上传信息
   * @param {Object} response - 创建文件的响应数据（partInfoList 中已包含 chunk）
   * @param {Object} params - 上传参数（包含 fileItem）
   * @param {Function} onContentProgress - 内容上传进度回调
   * @param {Function} [onChunkDone] - 单个分片上传完成回调 (chunkSizeBytes, durationMs) => void
   * @param {Function} onCancelCheck - 取消检查回调
   * @returns {Promise<{success: boolean, data: any, fileId: string|null, error: string|null}>}
   */
  async dealUploadPartInfo(response, params, onContentProgress, onChunkDone, onCancelCheck) {
    const fileId = response?.fileId || null;
    let uploadId = response?.uploadId || null;

    // 1. 如果 response 不为空且有分片信息，使用 response（新上传）
    // 如果 response 为空，则从本地存储读取数据（暂停后继续上传）
    let savedUploadData = null;

    if (response && response.partInfoList && response.partInfoList.length > 0) {
      // response 有分片信息，使用 response（新上传，response.partInfoList 中已包含 chunk）
      savedUploadData = {
        ...response,
      };
      logger.info(`使用 response 中的分片信息: fileId=${fileId}, 分片数=${response.partInfoList.length}`);
    } else if (fileId) {
      // response 为空或没有分片信息，从本地存储读取（暂停后继续上传）
      const storedData = await uploadStorage.getUploadData(fileId);
      if (storedData && storedData.uploadId && storedData.partInfoList) {
        savedUploadData = {
          fileId: storedData.fileId,
          uploadId: storedData.uploadId,
          partInfoList: storedData.partInfoList,
        };
        if (!uploadId) {
          uploadId = storedData.uploadId;
        }
        logger.info(`从本地存储读取上传数据: fileId=${fileId}`);
      }
    }

    const sumPartBytes = (list) => {
      if (!Array.isArray(list) || list.length === 0) return 0;
      return list.reduce((sum, part) => {
        const chunk = part?.chunk;
        const size =
          part?.partSize ??
          part?.part_size ??
          (chunk && typeof chunk.size === "number" ? chunk.size : 0) ??
          0;
        return sum + (Number.isFinite(size) ? size : 0);
      }, 0);
    };

    // 2. 通过 listUploadedParts 获取已上传分片列表，过滤掉已上传的分片
    let totalPartsCount = 0; // 原始分片总数
    let uploadedPartsCount = 0; // 已上传的分片数量
    let totalBytes = 0; // 文件总字节（按分片 size 汇总）
    let uploadedBytesBefore = 0; // 本次上传开始前已上传字节（续传/服务器已有分片）
    
    if (fileId && uploadId && savedUploadData && savedUploadData.partInfoList) {
      // 保存原始分片总数
      const originalPartInfoList = savedUploadData.partInfoList || [];
      totalPartsCount = originalPartInfoList.length;
      const fileSizeFromItem = params?.fileItem?.size;
      totalBytes =
        Number.isFinite(fileSizeFromItem) && fileSizeFromItem > 0
          ? fileSizeFromItem
          : sumPartBytes(originalPartInfoList);
      
      try {
        const uploadedPartsResult = await this.listUploadedParts(fileId, uploadId);
        if (uploadedPartsResult.success && uploadedPartsResult.data) {
          const serverUploadedParts = uploadedPartsResult.data;
          logger.info(`从服务器获取已上传分片: fileId=${fileId}, 服务器分片数=${serverUploadedParts.length}`);
          
          // 获取已上传的分片编号集合
          const uploadedPartNumbers = new Set(
            serverUploadedParts.map(part => part.part_number || part.partNumber).filter(Boolean)
          );
          
          // 记录已上传的分片数量
          uploadedPartsCount = uploadedPartNumbers.size;
          
          // 从 partInfoList 中过滤掉已上传的分片
          savedUploadData.partInfoList = savedUploadData.partInfoList.filter(
            partInfo => !uploadedPartNumbers.has(partInfo.partNumber ?? partInfo.part_number)
          );
          // 计算续传已上传字节（用过滤前后的剩余字节差值）
          const remainingBytes = sumPartBytes(savedUploadData.partInfoList);
          uploadedBytesBefore = Math.max(0, totalBytes - remainingBytes);
          
          logger.info(`过滤后需要上传的分片数: ${savedUploadData.partInfoList.length}, 已上传: ${uploadedPartsCount}, 总计: ${totalPartsCount}`);
        } else {
          logger.warn(`获取已上传分片失败: ${uploadedPartsResult.error}，将上传所有分片`);
          reportBehaviorLog({
            action: "upload_list_parts_fail",
            description: `fileId=${fileId || ""}, uploadId=${uploadId || ""}, error=${uploadedPartsResult?.error || ""}`,
          });
          uploadedPartsCount = 0;
          uploadedBytesBefore = 0;
        }
      } catch (error) {
        logger.error(`获取已上传分片异常: ${error.message}，将上传所有分片`);
        reportException(error, {
          action: "upload_list_parts_fail",
          description: `fileId=${fileId || ""}, uploadId=${uploadId || ""}`,
        });
        uploadedPartsCount = 0;
        uploadedBytesBefore = 0;
      }
    } else if (savedUploadData && savedUploadData.partInfoList) {
      // 新上传，没有已上传的分片
      totalPartsCount = savedUploadData.partInfoList.length;
      uploadedPartsCount = 0;
      const fileSizeFromItem = params?.fileItem?.size;
      totalBytes =
        Number.isFinite(fileSizeFromItem) && fileSizeFromItem > 0
          ? fileSizeFromItem
          : sumPartBytes(savedUploadData.partInfoList);
      uploadedBytesBefore = 0;
    }

    let contentResult = await this.uploadFileContent(
      savedUploadData,
      onContentProgress,
      onChunkDone,
      onCancelCheck,
      () => {
        // 暂停检查：检查 fileItem 的状态
        return params.fileItem?.uploadStatus === fileUtil.UPLOAD_STATUS.PAUSED;
      },
      totalPartsCount,
      uploadedPartsCount,
      totalBytes,
      uploadedBytesBefore
    );

    // 403 时返回 URL_EXPIRED，重新执行续传：获取最新上传地址、过滤已上传、写存储后再次上传
    if (!contentResult.success && contentResult.error === "URL_EXPIRED" && savedUploadData?.fileId && savedUploadData?.uploadId && savedUploadData?.partInfoList?.length) {
      logger.info("重新执行续传：获取最新上传地址后上传");
      return this.resumeUploadWithLatestUrls(savedUploadData, params, onContentProgress, onChunkDone, onCancelCheck);
    }

    if (!contentResult.success) {
      return {
        success: false,
        data: null,
        fileId: fileId,
        error: contentResult.error,
      };
    }

    // 4. 当上传成功后移除存储数据
    if (fileId) {
      try {
        await uploadStorage.deleteUploadData(fileId);
        await uploadStorage.deleteFileInfo(fileId);
        logger.info(`上传成功，已移除本地存储数据: fileId=${fileId}`);
      } catch (error) {
        logger.error("移除本地存储数据失败:", error);
        reportException(error, {
          action: "upload_storage_cleanup_fail",
          description: `fileId=${fileId || ""}`,
        });
        // 不影响上传成功的结果
      }
    }

    return {
      success: true,
      data: contentResult.data,
      fileId: fileId,
      error: null,
    };
  }

  /**
   * 上传分片
   * @param {string} uploadUrl - 上传URL
   * @param {Blob} chunk - 分片数据
   * @param {AbortSignal} signal - 取消信号（可选）
   * @param {Function} [onUploadProgress] - ({loaded, total}) => void，分片上传实时进度
   * @param {Function} [getAbortReason] - () => string|null，用于区分 PAUSED/取消
   * @returns {Promise<{success: boolean, etag: string|null, error: string|null}>} 上传结果
   */
  async uploadPart(uploadUrl, chunk, signal = null, onUploadProgress, getAbortReason) {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      try {
        xhr.open("PUT", uploadUrl, true);
        xhr.setRequestHeader("Accept", "application/json, text/plain, */*");

        if (xhr.upload && typeof onUploadProgress === "function") {
          xhr.upload.onprogress = (e) => {
            const loaded = typeof e.loaded === "number" ? e.loaded : 0;
            const total =
              typeof e.total === "number" && e.total > 0
                ? e.total
                : (chunk && typeof chunk.size === "number" ? chunk.size : 0);
            onUploadProgress({ loaded, total });
          };
        }

        xhr.onload = () => {
          const status = xhr.status;
          if (status >= 200 && status < 300) {
            let etag = xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag");
            if (etag) etag = etag.replace(/"/g, "");
            resolve({ success: true, etag, error: null });
            return;
          }
          reportBehaviorLog({
            action: "upload_part_http_fail",
            description: `status=${status}, statusText=${xhr.statusText || ""}`,
          });
          resolve({
            success: false,
            etag: null,
            error: `分片上传失败: ${status} ${xhr.statusText || ""}`.trim(),
            status,
          });
        };

        xhr.onerror = () => {
          const reason = typeof getAbortReason === "function" ? getAbortReason() : null;
          if (reason === "PAUSED") {
            resolve({ success: false, etag: null, error: "PAUSED" });
            return;
          }
          resolve({ success: false, etag: null, error: reason || "分片上传失败" });
        };

        xhr.onabort = () => {
          const reason = typeof getAbortReason === "function" ? getAbortReason() : null;
          resolve({ success: false, etag: null, error: reason || "用户取消上传" });
        };

        xhr.ontimeout = () => {
          resolve({ success: false, etag: null, error: "分片上传超时" });
        };

        if (signal) {
          const onAbort = () => {
            try {
              xhr.abort();
            } catch (_) {}
          };
          if (signal.aborted) {
            onAbort();
          } else {
            signal.addEventListener("abort", onAbort, { once: true });
            // eslint-disable-next-line no-inner-declarations
            const cleanup = () => signal.removeEventListener("abort", onAbort);
            xhr.addEventListener("loadend", cleanup, { once: true });
          }
        }

        xhr.send(chunk);
      } catch (e) {
        const reason = typeof getAbortReason === "function" ? getAbortReason() : null;
        resolve({ success: false, etag: null, error: reason || e?.message || "分片上传失败" });
      }
    });
  }

  /**
   * 上传文件内容（支持分片上传）
   * @param {Object} uploadData - 上传数据（包含分片信息等，已过滤掉已上传的分片）
   * @param {Function} onProgress - 进度回调函数 (progress: number) => void
   * @param {Function} [onChunkDone] - 分片上传完成回调 (chunkSizeBytes, durationMs) => void，用于按分片耗时计算速度
   * @param {Function} onCancelCheck - 取消检查函数 () => boolean
   * @param {Function} onPauseCheck - 暂停检查函数 () => boolean
   * @param {number} totalPartsCount - 原始分片总数（用于计算续传进度）
   * @param {number} uploadedPartsCount - 已上传的分片数量（用于计算续传进度）
   * @returns {Promise<{success: boolean, data: any, error: string|null}>} 上传结果
   */
  async uploadFileContent(
    uploadData,
    onProgress,
    onChunkDone,
    onCancelCheck,
    onPauseCheck,
    totalPartsCount = 0,
    uploadedPartsCount = 0,
    totalBytes = 0,
    uploadedBytesBefore = 0
  ) {
    try {
      // 若用户已取消，直接中断
      if (onCancelCheck && onCancelCheck()) {
        throw new Error("用户取消上传");
      }

      logger.info('uploadData:', uploadData)

      // 创建 AbortController 用于取消正在进行的网络请求
      const abortController = new AbortController();
      const signal = abortController.signal;
      let abortReason = null;

      // 检查是否有分片信息
      if (uploadData.partInfoList && uploadData.partInfoList.length > 0) {
        const partInfoList = uploadData.partInfoList;
        const remainingParts = partInfoList.length; // 剩余需要上传的分片数
        
        // 如果没有传入总分数，使用当前分片列表的长度（新上传）
        const totalParts = totalPartsCount > 0 ? totalPartsCount : remainingParts;
        const totalBytesAll =
          totalBytes > 0
            ? totalBytes
            : partInfoList.reduce((sum, part) => {
                const chunk = part?.chunk;
                const size =
                  part?.partSize ??
                  part?.part_size ??
                  (chunk && typeof chunk.size === "number" ? chunk.size : 0) ??
                  0;
                return sum + (Number.isFinite(size) ? size : 0);
              }, 0);
        const uploadedBytesBase = Math.max(0, uploadedBytesBefore || 0);
        let uploadedBytesInLoop = 0;
        let lastProgressEmitAt = 0;
        let lastProgressValue = -1;

        const emitOverallProgressByBytes = (baseBytes, loadedBytes) => {
          if (typeof onProgress !== "function" || totalBytesAll <= 0) return;
          const uploaded = Math.max(0, Math.min(totalBytesAll, baseBytes + loadedBytes));
          const raw = (uploaded / totalBytesAll) * 100;
          const rounded = Math.round(raw * 10) / 10;
          const now = Date.now();
          // 节流：避免 onprogress 过于频繁导致 UI 卡顿
          if (rounded === 100 || lastProgressValue < 0 || Math.abs(rounded - lastProgressValue) >= 0.1 || now - lastProgressEmitAt >= 120) {
            lastProgressValue = rounded;
            lastProgressEmitAt = now;
            onProgress(Math.max(0, Math.min(100, rounded)));
          }
        };

        // 上传所有分片（已过滤掉服务器已上传的分片）
        for (let i = 0; i < remainingParts; i++) {
          // 每一分片前检查是否已取消或暂停
          if (onCancelCheck && onCancelCheck()) {
            abortReason = "用户取消上传";
            abortController.abort(); // 取消正在进行的请求
            throw new Error("用户取消上传");
          }

          if (onPauseCheck && onPauseCheck()) {
            abortReason = "PAUSED";
            abortController.abort(); // 暂停时也取消正在进行的请求
            // 返回已上传的分片信息，供继续时使用
            return {
              success: false,
              data: null,
              error: "PAUSED",
            };
          }

          const partInfo = partInfoList[i];
          const partNumber = partInfo.partNumber ?? partInfo.part_number;
          const uploadUrl = partInfo.uploadUrl;
          // 直接使用 partInfo 中的 chunk
          const chunk = partInfo.chunk;
          const chunkSizeBytes = partInfo.partSize ?? partInfo.part_size ?? (chunk && chunk.size) ?? 0;
          const chunkStartTime = Date.now();

          // 创建一个 Promise，用于在上传过程中监听取消状态
          let checkInterval = null;
          const cancelPromise = new Promise((_, reject) => {
            // 定期检查取消状态（每50ms检查一次，更频繁的检查）
            checkInterval = setInterval(() => {
              if (signal.aborted) {
                clearInterval(checkInterval);
                reject(new Error(abortReason || "用户取消上传"));
              } else if (onCancelCheck && onCancelCheck()) {
                clearInterval(checkInterval);
                abortReason = "用户取消上传";
                abortController.abort();
                reject(new Error("用户取消上传"));
              } else if (onPauseCheck && onPauseCheck()) {
                clearInterval(checkInterval);
                abortReason = "PAUSED";
                abortController.abort();
                reject(new Error("PAUSED"));
              }
            }, 50);
          });

          try {
            const baseBytes = uploadedBytesBase + uploadedBytesInLoop;
            // 使用 Promise.race 来同时等待上传和取消检查
            // 如果取消检查先完成，会立即中断上传
            let partResult = await Promise.race([
              this.uploadPart(
                uploadUrl,
                chunk,
                signal,
                ({ loaded }) => {
                  if (totalBytesAll > 0) {
                    emitOverallProgressByBytes(baseBytes, loaded);
                  }
                },
                () => abortReason
              ).finally(() => {
                // 上传完成时清除检查定时器
                if (checkInterval) {
                  clearInterval(checkInterval);
                }
              }),
              cancelPromise.catch(error => {
                // 取消检查触发时，清除定时器并返回错误结果
                if (checkInterval) {
                  clearInterval(checkInterval);
                }
                return { success: false, error: error.message };
              })
            ]);

            // 如果分片上传被取消，立即返回
            if (!partResult.success && (partResult.error === "用户取消上传" || partResult.error === "PAUSED")) {
              if (partResult.error === "PAUSED") {
                return {
                  success: false,
                  data: null,
                  error: "PAUSED",
                };
              }
              throw new Error("用户取消上传");
            }

            // status=403 时不单分片重试，返回 URL_EXPIRED 由 dealUploadPartInfo 重新执行续传（获取最新上传地址后上传）
            if (!partResult.success && partResult.status === 403) {
              logger.info(`分片 ${partNumber} 返回 403，返回由 dealUploadPartInfo 重新执行续传`);
              return {
                success: false,
                data: null,
                error: "URL_EXPIRED",
              };
            }
            if (!partResult.success) {
              logger.error(`分片 ${partNumber} 上传失败: ${partResult.error}`);
              throw new Error(partResult.error);
            }

            // 更新进度（实时进度由 upload.onprogress 驱动；此处在分片完成时兜底更新一次）
            if (totalBytesAll > 0) {
              uploadedBytesInLoop += Number.isFinite(chunkSizeBytes) ? chunkSizeBytes : 0;
              emitOverallProgressByBytes(uploadedBytesBase + uploadedBytesInLoop, 0);
            } else if (typeof onProgress === "function" && totalParts > 0) {
              if (!onCancelCheck || !onCancelCheck()) {
                const currentUploadedCount = uploadedPartsCount + (i + 1);
                const rawProgress = (currentUploadedCount / totalParts) * 100;
                const roundedProgress = Math.round(rawProgress * 10) / 10;
                onProgress(Math.max(0, Math.min(100, roundedProgress)));
              }
            }
            // 按分片实际上传耗时上报，用于准确计算速度与剩余时间
            if (typeof onChunkDone === "function" && chunkSizeBytes > 0) {
              const durationMs = Date.now() - chunkStartTime;
              onChunkDone(chunkSizeBytes, durationMs);
            }
          } catch (error) {
            // 清除定时器
            if (checkInterval) {
              clearInterval(checkInterval);
            }
            // 如果是取消或暂停，直接抛出
            if (error.message === "用户取消上传" || error.message === "PAUSED") {
              if (error.message === "PAUSED") {
                return {
                  success: false,
                  data: null,
                  error: "PAUSED",
                };
              }
              throw error;
            }
            // 其他错误继续处理
            throw error;
          }
        }

        // 分片全部完成后再次检查取消和暂停状态
        if (onCancelCheck && onCancelCheck()) {
          throw new Error("用户取消上传");
        }
        
        if (onPauseCheck && onPauseCheck()) {
          return {
            success: false,
            data: null,
            error: "PAUSED",
          };
        }

        const result = await this.completeUpload(
          uploadData.fileId,
          uploadData.uploadId
        );

        if (result.success) {
          return { success: true, data: result.data };
        } else {
          reportBehaviorLog({
            action: "upload_complete_fail",
            description: `fileId=${uploadData?.fileId || ""}, uploadId=${uploadData?.uploadId || ""}, error=${result?.error || ""}`,
          });
          throw new Error(result.error || "完成上传失败");
        }
      } else {
        // 未找到分片信息，可能文件已存在或不需要上传
        return { success: true, data: null };
      }
    } catch (error) {
      // 取消/暂停不按异常上报（避免噪音），其他异常补一条
      if (error?.message !== "用户取消上传" && error?.message !== "PAUSED") {
        reportException(error, {
          action: "upload_content_fail",
          description: `fileId=${uploadData?.fileId || ""}, uploadId=${uploadData?.uploadId || ""}`,
        });
      }
      return {
        success: false,
        data: null,
        error: error.message || "文件内容上传失败",
      };
    }
  }

  /**
   * 获取已上传的分片列表
   * @param {string} fileId - 文件ID
   * @param {string} uploadId - 上传ID
   * @returns {Promise<{success: boolean, data: Array|null, error: string|null}>} 返回已上传的分片列表
   */
  async listUploadedParts(fileId, uploadId) {
    try {
      const params = {
        fileId: fileId,
        uploadId: uploadId,
      };
      const result = await this.post("/api/file/listUploadedParts", params);
      
      if (result.success && result.data && result.data.uploaded_parts) {
        // 返回已上传的分片列表，格式可能是 [{part_number: 1, etag: "xxx"}, ...]
        const uploadedParts = result.data.uploaded_parts.length > 0 ? result.data.uploaded_parts : [];
        return {
          success: true,
          data: uploadedParts,
          error: null,
        };
      } else {
        return {
          success: false,
          data: null,
          error: result.error || "获取已上传分片列表失败",
        };
      }
    } catch (error) {
      return {
        success: false,
        data: null,
        error: error.message || "获取已上传分片列表异常",
      };
    }
  }

  /**
   * 完成上传
   * @param {string} fileId - 文件ID
   * @param {string} uploadId - 上传ID
   * @returns {Promise<{success: boolean, data: any, error: string|null}>} 请求结果
   */
  async completeUpload(fileId, uploadId) {
    const params = {
      fileId: fileId,
      uploadId: uploadId,
    };
    return this.post("/api/file/completeUpload", params);
  }

  /**
   * 403 时获取最新分片上传地址
   * @param {string} fileId - 文件ID
   * @param {string} uploadId - 上传ID
   * @param {Array} partInfoList - 分片列表，每项含 partNumber、partSize
   * @returns {Promise<{success: boolean, partInfoList: Array|null, error: string|null}>} 返回带 upload_url 的 part_info_list
   */
  async getUploadUrl(fileId, uploadId, partInfoList) {
    const part_info_list = (partInfoList || []).map((p) => ({
      part_number: p.partNumber ?? p.part_number,
      part_size: p.partSize ?? p.part_size,
    })).filter((p) => p.part_number != null && p.part_size != null);
    try {
      const result = await this.post("/api/file/getUploadUrl", {
        file_id: fileId,
        upload_id: uploadId,
        part_info_list,
      });
      const list = result.success && result.data && (result.data.part_info_list ?? result.data.partInfoList);
      if (Array.isArray(list)) {
        return { success: true, partInfoList: list, error: null };
      }
      return {
        success: false,
        partInfoList: null,
        error: result.error || "获取分片上传地址失败",
      };
    } catch (error) {
      return {
        success: false,
        partInfoList: null,
        error: error.message || "获取分片上传地址异常",
      };
    }
  }

  /**
   * 获取文件列表
   * @param {object} uploadParams - 上传参数
   * @returns {Promise<{success: boolean, data: any, error: string|null}>}
   */
  async getListData(uploadParams) {
    const params = {
      parentFileId: uploadParams.parentFileId ? uploadParams.parentFileId : '',
      limit: 100,
      marker: ''
    };

    const result = await this.get("/api/share/file/getFileList", params);

    if (result.success) {
      // 处理成功情况
      // logger.info("获取文件列表成功:", result.data);
      const nUploadParams = JSON.parse(JSON.stringify(uploadParams));
      const nItems = [];

      const data = result.data

      if (data.items && data.items.length > 0) {
        for (let i = 0; i < data.items.length; i++) {
          const item = data.items[i];
          if (item.name && item.fileId) {
            const itemData = {
              uploadStatus: fileUtil.UPLOAD_STATUS.SUCCESS,
              name: item.name,
              size: item.size,
              fileId: item.fileId,
              downloadCount: 0,
            };
            nItems.push(itemData);
          }
        }
      }

      nUploadParams.items = nItems;
      sessionStorage.setItem(uploadStorage.getParamsKey(), JSON.stringify(nUploadParams));

      return {
        success: true,
        data: nUploadParams,
        error: null,
      };
    } else {
      // 处理失败情况
      logger.error("获取文件列表失败:", result.error);
      return {
        success: false,
        data: null,
        error: result.error || "未知错误",
      };
    }
  }

  /**
   * 自动登录
   * @param {string} authCode - 授权码
   * @param {boolean} isShare - 是否是分享链接
   * @returns {Promise<{success: boolean, data: any, error: string|null}>}
   */
  async autoLogin(authCode, isShare = false) {
    // 测试模式
    // if (authCode === "123456") {
    //   const uploadParams = {
    //     maxFileSize: "5GB",
    //     maxTotalSize: "5GB",
    //     allowMultiple: true,
    //     encryption: true,
    //     description: "测试模式：支持拖拽上传，最大单文件5GB",
    //     accessToken:
    //       "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiI4NmYxYTcwMzZkZjc0ZjJjYTRkOGI0ZmIwZWZkNzFmZSIsImN1c3RvbUpzb24iOiJ7XCJjbGllbnRJZFwiOlwiMjVkelgzdmJZcWt0Vnh5WFwiLFwiZG9tYWluSWRcIjpcImJqMjlcIixcInNjb3BlXCI6W1wiRFJJVkUuQUxMXCIsXCJTSEFSRS5BTExcIixcIkZJTEUuQUxMXCIsXCJVU0VSLkFMTFwiLFwiVklFVy5BTExcIixcIlNUT1JBR0UuQUxMXCIsXCJTVE9SQUdFRklMRS5MSVNUXCIsXCJCQVRDSFwiLFwiT0FVVEguQUxMXCIsXCJJTUFHRS5BTExcIixcIklOVklURS5BTExcIixcIkFDQ09VTlQuQUxMXCIsXCJTWU5DTUFQUElORy5MSVNUXCIsXCJTWU5DTUFQUElORy5ERUxFVEVcIl0sXCJyb2xlXCI6XCJ1c2VyXCIsXCJyZWZcIjpcImh0dHBzOi8vd3d3LmFsaXBhbi5jb20vXCIsXCJkZXZpY2VfaWRcIjpcImIwMDVjNzk2YmQwMDQxNzE4Y2ZkZmY1NzdmOTY1YWU5XCJ9IiwiZXhwIjoxNzYzNDQ5NTExLCJpYXQiOjE3NjM0NDIyNTF9.C_yJ76tUdOtZMsQSkJDvso7Sw0onJ7BtbFEz5oPTBCXhXmaBpXIMLnWo0UWlx9ktWMRHgqkDo5UVlTKHFcZukDPb_VHVPjo8XsDuH6Arzt55AXD1WT_50dsHf1ainBlr_Bf2FfLKW5-_meXxQYPDo2RJfVpNZjc2XxlDj_yG2bI",
    //     driveId: "379647292",
    //     parentFileId: "690ab3a10f00b78a04674f1daf8595c67d18ddd1",
    //     shareLink: "http://127.0.0.1:3000/download",
    //   };

    //   return {
    //     success: true,
    //     data: uploadParams,
    //     error: null,
    //   };
    // }

    // 普通登录
    let url = "/auth/login"
    if (isShare) {
      url = "/auth/loginLink"
    }
    const result = await this.post(url, authCode);
    if (result.success) {
      const token = result.data
      
      // 登录成功后，将token设置到默认headers中，后续请求自动使用
      this.setDefaultHeaders({
        Authorization: token,
      });

      // 登录成功后再初始化行为/异常上报
      setBehaviorLogAuthToken(token)
      initBehaviorLog()
      
      let uploadParams = {
        accessToken: token,
      }
      // if (!isShare) {
      uploadParams = await this.getShareInfo(uploadParams, isShare)
      logger.info('uploadParams:', uploadParams)
      // }

      return {
        success: true,
        data: uploadParams,
        error: null,
      }
    }
    return result;
  }

  /**
   * 获取分享信息
   * @param {object} uploadParams - 上传参数
   * @param {boolean} isShare - 是否是分享链接
   * @returns {Promise<{success: boolean, data: any, error: string|null}>}
   */
  async getShareInfo(uploadParams, isShare = false) {
    let url = "/api/orders/getOrderInfo"
    if (isShare) {
      url = "/api/share/file/getOrderInfo"
    }
    const result = await this.get(url);
    logger.info('result:', result)
    let shareLink = ''
    let maxTotalSize = ''
    let expirationDate = '-'
    if (result.success) {
      const shareInfoData = result.data
      shareLink = shareInfoData.temporaryLink
      maxTotalSize = shareInfoData.diskSize + 'GB'
      expirationDate = shareInfoData.saveDays === -1 ? '永久' : (shareInfoData.orderEndTime || '-')
    }
    let nUploadParams = JSON.parse(JSON.stringify(uploadParams));
    nUploadParams.shareLink = shareLink
    nUploadParams.maxTotalSize = maxTotalSize
    nUploadParams.expirationDate = expirationDate
    return nUploadParams;
  }

  /**
   * 自动执行登录和获取列表
   * @param {string} authCode - 授权码（可选，如果不提供则从localStorage获取）
   * @param {boolean} isShare - 是否是分享链接
   * @returns {Promise<{success: boolean, uploadParams: any, error: string|null}>}
   */
  async autoLoginAndGetList(authCode = null, isShare = false) {
    // 如果没有提供authCode，尝试从localStorage获取
    const code = authCode || localStorage.getItem("lastAuthCode");

    if (!code) {
      return {
        success: false,
        uploadParams: null,
        error: "未找到授权码",
      };
    }

    // 1. 先执行登录
    const loginResult = await this.autoLogin(code, isShare);

    if (!loginResult.success) {
      return {
        success: false,
        uploadParams: null,
        error: loginResult.error || "登录失败",
      };
    }

    let uploadParams = loginResult.data;

    // 2. 如果有parentFileId，则获取文件列表
    const listResult = await this.getListData(uploadParams);

    if (!listResult.success) {
      // 获取列表失败，但登录成功，返回登录参数
      return {
        success: false,
        uploadParams: uploadParams,
        error: `当前用户异常，请联系商家处理！`,
      };
    }

    uploadParams = listResult.data;
    
    // 没有parentFileId，只返回登录参数
    return {
      success: true,
      uploadParams: uploadParams,
      error: null,
    };
  }
}

// 创建默认实例
const request = new Request();

// 导出类和实例
export default request;
export { Request };
